import { useEffect, useRef, useState } from 'react';
import OrderBasketItem from '../components-library/basket/order-basket-item';
import { DeliveryMode, ERROR_CODES, ORDER_METHODS, SCREENS } from '../services/exports/Constants';
import { useSelector } from 'react-redux';
import useScreenType from '../hooks/utility/useScreenType';
import DeliveryThreshold from '../components-library/delivery/DeliveryThreshold';
import OrderValidationManager from '../services/api/OrderValidationManager';
import CheckoutThreshold from '../components-library/checkout/CheckoutThreshold';
import { Trans, useTranslation } from 'react-i18next';
import useInitialData from '../hooks/global/useInitialData';
import useRouter from '../hooks/navigation/useRouter';
import useBasket from '../hooks/basket/useBasket';
import { StoreInterface } from '../store/types';
import EmptyBasketIllustration from '../assets/illustrations/EmptyBasketIllustration';
import OrderType from '../components-library/order-type/OrderType';
import useAppMethods from '../hooks/utility/useAppMethods';
import moment from 'moment';
import PromoCodeInput from '../components-library/checkout/PromoCodeInput';
import classnames from 'classnames';
import useOrderFlow from '../hooks/order/useOrderFlow';
import { CompleteBasketItem } from '../providers/BasketProvider';
import { ReservedHttpResponseCodes, ValidationErrorResponseData } from '../services/exports/Types';
import collect from 'collect.js';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import useIsCheckoutDisabled from '../hooks/basket/useIsCheckoutDisabled';
import RecommendedProducts from '../modules/mobile/features/shared/components/recommended-products/RecommendedProducts';
import useMenu from '../hooks/menu/useMenu';
import PageHeader from '../components-library/headers/PageHeader';
import CheckoutButton from '../components-library/buttons/CheckoutButton';
import useAnalytics from '../hooks/analytics/useAnalytics';
import useCompanyHours from '../hooks/availability/useCompanyHours';

interface Props {
  customParentContainer?: string;
  customParentStyle?: object;
  onBasketItemUpdateClick?: (index: number, item: CompleteBasketItem) => void;
}

export default function OrderBasketScreen(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderBasketScreen' });

  const { customParentContainer, customParentStyle, onBasketItemUpdateClick } = props;

  const { data, refresh } = useInitialData();
  const { company, franchise } = data;
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const { open } = useCompanyHours();
  const { isDesktop, isMobile } = useScreenType();
  const { navigate } = useRouter();
  const { isMobileApp } = useMobileApp();
  const hasCheckoutNavBar = !isMobileApp && !isDesktop;
  const { trackBeginCheckout } = useAnalytics();
  const { data: orderData } = useOrderFlow();

  const { isOrderMethodInfoComplete, getOrderData, update, resetOrder } = useOrderFlow();
  const { items, refreshBasket } = useBasket();
  const { toggleOrderMethodModal, toggleMenuItemModal } = useAppMethods();
  const { getUpsellModifierGroup } = useMenu();
  const upsellModifierGroup = getUpsellModifierGroup(items);
  const hasUpsellModifierGroup = !isDesktop && !!upsellModifierGroup?.modifiers?.length;

  const [validating, setValidating] = useState(false);

  type PromoCodeInputRefType = {
    setPromoCode: (promoCode: any) => void;
  };

  const promoCodeInputRef = useRef<PromoCodeInputRefType>();

  const orderMethodInfoComplete =
    isOrderMethodInfoComplete &&
    (cached_order?.method === ORDER_METHODS.PICKUP ||
      (cached_order?.method === ORDER_METHODS.ROOM_SERVICE && !!cached_order?.room_service_location_id) ||
      (cached_order?.method === ORDER_METHODS.DELIVERY &&
        company?.delivery_mode === DeliveryMode.Internal &&
        !!cached_order?.delivery_zone?.id) ||
      (cached_order?.method === ORDER_METHODS.DELIVERY &&
        company?.delivery_mode === DeliveryMode.External &&
        !!cached_order?.delivery_zone?.id &&
        !!cached_order?.delivery_quote?.id));

  useEffect(() => {
    window.scrollTo(0, 0);
    refreshBasket();
  }, []);

  async function validateBeforeProceeding(): Promise<void> {
    setValidating(true);
    if (!(await isValid())) {
      return setValidating(false);
    }
    setValidating(false);

    navigate({
      web: {
        to: SCREENS.CHECKOUT,
      },
    });
    trackBeginCheckout(orderData, items);
  }

  async function isValid(): Promise<boolean> {
    if (!orderMethodInfoComplete && franchise?.has_multiple_companies) {
      toggleOrderMethodModal({ openModal: true });
      return false;
    }

    if (
      cached_order.method === ORDER_METHODS.ROOM_SERVICE &&
      franchise?.has_multiple_companies &&
      ((!company?.table_number_hidden ? !cached_order.room_service_details : false) ||
        !cached_order.room_service_location_id)
    ) {
      toggleOrderMethodModal({ openModal: true });
      return false;
    }

    if (cached_order?.scheduled_for && moment(cached_order?.scheduled_for).isBefore(moment())) {
      handleOrderTimeNotAvailable();

      return false;
    }

    const ignoredValidationExceptions: string[] = collect()
      .when(!open, (collection) =>
        collection.merge([
          ERROR_CODES.PICKUP_CLOSED,
          ERROR_CODES.DELIVERY_CLOSED,
          ERROR_CODES.ORDER_TIME_NOT_AVAILABLE,
          ERROR_CODES.ITEMS_MENU_TIME_UNAVAILABLE,
        ]),
      )
      .toArray();

    const onValidationError = async (response: ValidationErrorResponseData): Promise<boolean> => {
      if (collect(response.rules).has('customer_phone_number')) {
        update('customer_phone_number', null);

        const { success } = await OrderValidationManager.validate(
          getOrderData({
            customer_phone_number: null,
            ignored_error_codes: ignoredValidationExceptions,
          }),
          {
            [ERROR_CODES.ORDER_TIME_NOT_AVAILABLE]: handleOrderTimeNotAvailable,
            [ERROR_CODES.PICKUP_CLOSED]: handleOrderTimeNotAvailable,
            [ERROR_CODES.DELIVERY_CLOSED]: handleOrderTimeNotAvailable,
            [ERROR_CODES.DELIVERY_UNAVAILABLE]: handleOrderTimeNotAvailable,
            [ERROR_CODES.ITEMS_OUT_OF_STOCK]: handleItemsOutOfStock,
            [ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE]: handleAddressOutsideDeliveryArea,
          },
        );

        if (!success) {
          resetOrder({ customer_phone_number: null });

          return false;
        }

        return success;
      }

      if (
        collect(response.rules)
          .only([
            'room_service_location_id',
            'delivery_quote_id',
            'delivery_street_name',
            'delivery_street_number',
            'delivery_city',
            'delivery_zip_code',
          ])
          .isNotEmpty()
      ) {
        toggleOrderMethodModal({ openModal: true });
        return false;
      }

      return true;
    };

    const { success, response, status } = await OrderValidationManager.validate(
      getOrderData({ ignored_error_codes: ignoredValidationExceptions }),
      {
        [ERROR_CODES.ORDER_TIME_NOT_AVAILABLE]: handleOrderTimeNotAvailable,
        [ERROR_CODES.PICKUP_CLOSED]: handleOrderTimeNotAvailable,
        [ERROR_CODES.DELIVERY_CLOSED]: handleOrderTimeNotAvailable,
        [ERROR_CODES.DELIVERY_UNAVAILABLE]: handleOrderTimeNotAvailable,
        [ERROR_CODES.ITEMS_OUT_OF_STOCK]: handleItemsOutOfStock,
        [ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE]: handleAddressOutsideDeliveryArea,
      },
    );

    if (status === ReservedHttpResponseCodes.ValidationError) {
      return onValidationError(response.response.data);
    }

    return success;
  }

  function handleOrderTimeNotAvailable(): void {
    navigate({
      web: {
        to: SCREENS.HOME_SCREEN,
      },
    });
    toggleOrderMethodModal({ openModal: true });
  }

  function handleItemsOutOfStock(): Promise<void> | void {
    return refresh().then(() =>
      navigate({
        web: {
          to: SCREENS.HOME_SCREEN,
        },
      }),
    );
  }

  function handleAddressOutsideDeliveryArea(): void {
    navigate({
      web: {
        to: SCREENS.HOME_SCREEN,
      },
    });
    toggleOrderMethodModal({ openModal: true });
  }

  const isCheckoutDisabled = useIsCheckoutDisabled();

  const renderEmptyBasket = (): JSX.Element => {
    return (
      <div className="grow w-full flex flex-col justify-center items-center z-1">
        <EmptyBasketIllustration />
        <p className="text-center text-sm text-gray-700">
          <Trans t={t}>empty_order_basket.description</Trans>
        </p>
      </div>
    );
  };

  const onBasketItemUpdate = (index: number, item: CompleteBasketItem): void => {
    if (onBasketItemUpdateClick) {
      return onBasketItemUpdateClick(index, item);
    }

    toggleMenuItemModal({
      openModal: true,
      product: item.entry,
      isFreeItem: item.is_free,
      lineItem: item,
      basketItemIndex: index,
    });
  };

  return (
    <div
      className={classnames('lg:h-screen h-full flex flex-col relative overflow-hidden', customParentContainer)}
      style={customParentStyle}
    >
      {hasCheckoutNavBar && (
        <PageHeader.Back
          onClick={() =>
            navigate({
              web: { to: SCREENS.HOME_SCREEN },
            })
          }
          title={t('buttons.menu')}
        />
      )}
      <div
        className={classnames('overflow-y-auto scrollbar-hide p-md h-full pb-[240px]', {
          'space-y-md flex-1 bg-gray-600': isMobile,
          'flex-1': isMobile,
          'space-y-md flex-initial': isDesktop,
        })}
      >
        <div className={classnames('rounded-2xl')}>
          {!isMobileApp && <OrderType className="mb-md border-none bg-white" />}

          <PromoCodeInput
            ref={promoCodeInputRef}
            className={classnames('rounded-lg p-sm bg-white')}
            titleClassName={classnames({ 'text-gray-700': isMobileApp })}
          />
        </div>
        {items.length > 0 ? (
          <div className="space-y-md">
            {items.map((item: CompleteBasketItem, index) => (
              <OrderBasketItem
                key={`basket-item-${index}`}
                coupon_type={cached_order?.coupon_type}
                data={item}
                index={index}
                editable
                onUpdateClick={() => onBasketItemUpdate(index, item)}
              />
            ))}
            {hasUpsellModifierGroup && (
              <RecommendedProducts className="-mx-md mt-md px-md py-lg bg-brand-primary/30">
                <RecommendedProducts.Header
                  title={t('upsell.title')}
                  className={classnames({ '!text-base': isMobile })}
                />
                <RecommendedProducts.Content products={upsellModifierGroup.modifiers} />
              </RecommendedProducts>
            )}
            <DeliveryThreshold />
            <CheckoutThreshold />
          </div>
        ) : (
          renderEmptyBasket()
        )}
        {isDesktop && !!items?.length && (
          <CheckoutButton
            className="absolute bottom-0 left-0 bg-white right-0 pb-md border-t-xs border-gray"
            title={t('buttons.checkout')}
            onClick={validateBeforeProceeding}
            hideAddProductsButton
            loading={validating}
            disabled={isCheckoutDisabled}
          />
        )}
      </div>
      {!isMobileApp && isMobile && (
        <CheckoutButton
          className="absolute bottom-0 left-0 bg-white right-0 pb-md border-t-xs border-gray"
          title={t('buttons.checkout')}
          onClick={validateBeforeProceeding}
          hideAddProductsButton
          loading={validating}
          disabled={isCheckoutDisabled}
        />
      )}
    </div>
  );
}
